/**
 * dashboard/loadHasFriends.ts — the /app landing's friendless-signal loader.
 *
 * DashboardHero's "From friends" section has three states: a friendless-user CTA
 * (invite / find), the passive feed doorway, and the live activity strip. Only
 * the CTA needs a genuine "you have zero active friends" signal — the strip is
 * driven by loaded activity (loadFriendStrip), and `friendItems.length === 0`
 * means "no activity loaded", NOT "no friends" (DashboardHero.tsx). This helper
 * fetches that boolean through the same authed oRPC client the friends surface
 * uses (`hasActiveFriends` → `friend.hasFriends`, a boolean-only read).
 *
 * Per Rule 8 (no silent swallow) a read failure is LOGGED and resolves to
 * `undefined` — "unknown" — never `false`. `undefined` maps to the passive
 * doorway in DashboardHero, so a transient failure NEVER flashes the CTA at an
 * active (befriended) user. It is a read, not a mutation, so it degrades quietly
 * (no toast).
 *
 * A small, pure-ish function (not a hook) so the success / error-degrades
 * behaviour is unit-testable without rendering the route. It invents no endpoint.
 *
 * Refs: branding.md §5 (quiet, honest state), CLAUDE.md Rule 8 (no silent swallow).
 */

import { hasActiveFriends } from "~/features/friends/api";
import { isNavigationAbort } from "~/lib/nav-abort";

/**
 * Resolve whether the caller has at least one active friend. Returns the boolean
 * on success, or `undefined` on failure (logged, never thrown) so the caller
 * falls back to its neutral state rather than asserting friendlessness.
 *
 * `load` is injectable for tests; it defaults to the real `hasActiveFriends`.
 */
export async function loadHasFriends(
  load: () => Promise<boolean> = hasActiveFriends,
): Promise<boolean | undefined> {
  try {
    return await load();
  } catch (err) {
    // Rule 8: a failed read is logged, never silently swallowed — but it is a
    // read, so it degrades to "unknown" (→ passive doorway), never `false`. A
    // nav-teardown abort (rapid route change) is expected noise → debug; a
    // genuine failure stays an error.
    if (isNavigationAbort(err)) {
      console.debug({ event: "dashboard_has_friends_load_aborted", err });
    } else {
      console.error({ event: "dashboard_has_friends_load_failed", err });
    }
    return undefined;
  }
}
